import React from 'react';
import { createRoot } from 'react-dom/client';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import { getConversionFactor } from '../../../../utils/conversionUtils.js';
import { getIFMVUtil } from '../../../../utils/getAVUtil.js';
import { usePersistedDataStore } from '../../../store.js';

const DeepReductionConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  reductionPercent,
  offerDisplay,
  ifmv,
}) => {
  if (!open) return null;

  const reductionLabel = Number.isFinite(reductionPercent)
    ? `${reductionPercent.toFixed(1)}%`
    : '> 75%';

  return (
    <StableMuiDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
        Confirm Large Offer Reduction
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
          This offer implies a {reductionLabel} reduction
          {Number.isFinite(ifmv) && ifmv > 0
            ? ` from IFMV ($${Math.round(ifmv).toLocaleString()})`
            : ''}
          {offerDisplay ? ` to ${offerDisplay}` : ''}
          . Are you sure you want to continue?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel ?? onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="warning">
          Confirm Offer
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

/** Last gate after validateAndAdjustOfferValue (450k IFMV / 25% floor). offerValue is always AV. */
export async function confirmIfDeepReduction(offerValue, row) {
  const ifmv = Number(getIFMVUtil(row) ?? row?.IFMV);
  const offerAV = Number(offerValue);
  if (!Number.isFinite(offerAV) || offerAV <= 0 || !Number.isFinite(ifmv) || ifmv <= 0) return true;
  if (row?.EQ == null || row?.RAR == null) return true;
  const loa = getConversionFactor(Number(row.EQ), Number(row.RAR));
  if (!Number.isFinite(loa) || loa <= 0) return true;
  const reductionPercent = ((ifmv - offerAV / loa) / ifmv) * 100;
  if (reductionPercent <= 75) return true;

  const isAVMode = !!usePersistedDataStore.getState().settlementIsAVMode;
  const offerDisplay = isAVMode
    ? `${Math.round(offerAV).toLocaleString()} AV`
    : `$${Math.round(offerAV / loa).toLocaleString()}`;

  return new Promise((resolve) => {
    const el = document.createElement('div');
    document.body.appendChild(el);
    const root = createRoot(el);
    const finish = (ok) => {
      root.unmount();
      el.remove();
      resolve(ok);
    };
    root.render(
      <DeepReductionConfirmDialog
        open
        reductionPercent={reductionPercent}
        offerDisplay={offerDisplay}
        ifmv={ifmv}
        onConfirm={() => finish(true)}
        onCancel={() => finish(false)}
        onClose={() => finish(false)}
      />
    );
  });
}

export default DeepReductionConfirmDialog;
